import dayjs, { type Dayjs } from "dayjs";
import type { NumberFormatOptions } from "@formatjs/ecma402-abstract";
import type {
  CheckFragment,
  PayrollEntryDetailsFragment,
} from "../../../../lib/graphql/generated";
import { amountToWordsEs } from "../../../../lib/num-words-es";
import { formatEmployeeName } from "../../../../lib/formats/employees";
import { formatPayeeName } from "../../../../lib/formats/finance";
import type { CheckSetupValues } from "./CheckSetup";
import type { useFormatMoney } from "../../../../lib/formats/money";
import { getSelectedEmployees } from "./utils";

type EmployeeComponent = NonNullable<
  PayrollEntryDetailsFragment["employees"][number]["components"]
>[number];

const DECIMAL_FORMAT_OPTIONS: NumberFormatOptions = {
  style: "decimal",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
};
const VOUCHER_FORMAT_OPTIONS = {
  ...DECIMAL_FORMAT_OPTIONS,
  useGrouping: false,
};

export type CheckTemplateFields = {
  LOCATION: string;
  ISSUE_DATE: string;
  AMOUNT_NUMERIC: string;
  PAYEE_NAME: string;
  AMOUNT_WORDS: string;
  PLANILLA_NUMBER?: string;
  PERIOD_START?: string;
  PERIOD_END?: string;
  LIQUIDO_VALUE?: string;
  MEMO?: string;
};

export interface CheckRenderContext {
  employeeId: string;
  fields: CheckTemplateFields;
}

interface BuildCheckRenderContextsParams {
  entry: PayrollEntryDetailsFragment;
  setupValues?: CheckSetupValues;
  formatMoney: ReturnType<typeof useFormatMoney>;
}

export function buildCheckRenderContexts({
  entry,
  setupValues,
  formatMoney,
}: BuildCheckRenderContextsParams): CheckRenderContext[] {
  if (!setupValues) {
    return [];
  }

  const employees = getSelectedEmployees(
    entry.employees,
    setupValues.employeeIds
  );

  return employees.map((employee) => {
    const components = (employee.components ?? []) as EmployeeComponent[];

    const selectedComponent = components.find(
      (component) =>
        component.salaryComponent.id === setupValues.salaryComponentId
    );

    const total = selectedComponent?.value ?? 0;
    const amountNumeric = formatMoney(total, undefined, DECIMAL_FORMAT_OPTIONS);
    const totalVoucher = formatMoney(total, undefined, VOUCHER_FORMAT_OPTIONS);
    const amountWords = amountToWordsEs(total);
    const payeeName = formatEmployeeName(employee.employee) ?? "";
    const planillaNumber = entry.id;

    const fields: CheckTemplateFields = {
      LOCATION: "Guatemala",
      ISSUE_DATE: formatIssueDate(setupValues.checkDate),
      AMOUNT_NUMERIC: amountNumeric,
      PAYEE_NAME: payeeName,
      AMOUNT_WORDS: amountWords.toLowerCase(),
      PLANILLA_NUMBER: planillaNumber,
      PERIOD_START: formatPayrollDate(entry.startDate),
      PERIOD_END: formatPayrollDate(entry.endDate),
      LIQUIDO_VALUE: totalVoucher,
    };

    return {
      employeeId: employee.id,
      fields,
    };
  });
}

function formatPayrollDate(date?: string | null) {
  if (!date) {
    return "";
  }

  return dayjs(date).format("DD/MM/YYYY");
}

function formatIssueDate(date?: string | null | Dayjs) {
  if (!date) {
    return "";
  }

  return dayjs(date).format("DD [de] MMMM [del Año] YYYY");
}

export interface VendorCheckData {
  payeeName: string;
  amount: number;
  checkDate: Dayjs;
  checkNumber: number;
  bankName?: string;
  memo?: string;
  voucherMemo?: string;
}

export function buildCheckRenderContextFromRecord(
  check: CheckFragment,
  formatMoney: ReturnType<typeof useFormatMoney>
): CheckRenderContext {
  const currency = check.amount.currency.isoCode;
  const total = check.amount.amount;
  const amountNumeric = formatMoney(total, currency, DECIMAL_FORMAT_OPTIONS);
  const amountWords = amountToWordsEs(total);

  const isPayrollSource = check.source?.__typename === "PayrollEntry";

  const fields: CheckTemplateFields = {
    LOCATION: "Guatemala",
    ISSUE_DATE: formatIssueDate(check.issueDate),
    AMOUNT_NUMERIC: amountNumeric,
    PAYEE_NAME: formatPayeeName(check.payee),
    AMOUNT_WORDS: amountWords.toLowerCase(),
    PLANILLA_NUMBER:
      check.source?.__typename === "PayrollEntry" ? check.source.id : undefined,
    LIQUIDO_VALUE: isPayrollSource
      ? formatMoney(total, currency, VOUCHER_FORMAT_OPTIONS)
      : undefined,
    MEMO: check.memo ?? undefined,
  };

  return { employeeId: check.id, fields };
}

export function buildVendorCheckRenderContext(
  data: VendorCheckData,
  formatMoney: ReturnType<typeof useFormatMoney>
): CheckRenderContext {
  const amountNumeric = formatMoney(
    data.amount,
    undefined,
    DECIMAL_FORMAT_OPTIONS
  );
  const amountWords = amountToWordsEs(data.amount);

  const fields: CheckTemplateFields = {
    LOCATION: "Guatemala",
    ISSUE_DATE: formatIssueDate(data.checkDate),
    AMOUNT_NUMERIC: amountNumeric,
    PAYEE_NAME: data.payeeName,
    AMOUNT_WORDS: amountWords.toLowerCase(),
    MEMO: data.voucherMemo,
  };

  return {
    employeeId: "vendor-payee",
    fields,
  };
}
